/**
 * Forward paper test of "Crash Bounce" (CB1), from research/dip-buyer
 * (RESULTS.md, PR #155): when a top coin closes an hour 25% or more below its
 * highest price of the past 7 days, buy at the next hour's open, then sell at
 * +10%, at −30%, or after 7 days, whichever comes first.
 *
 * The rule was found by a backtest after its test period had been seen, so it
 * is only judged on crashes that happen after its registration time. Nothing
 * here trades: it records what the rule WOULD have bought and sold, at public
 * Coinbase prices. Pure logic: no network, no storage.
 *
 * Timing matches the backtest exactly (research/dip-buyer/engine.ts):
 * - Hourly candles are keyed by bucket START; a bar's close is only known
 *   once the bar has ended, so a signal on bar i fills at bar i+1's open.
 * - The 7-day high is the highest high of the last 168 bars, inclusive.
 * - Exits are checked from the entry bar on. If one bar touches both the stop
 *   and the target, the stop is assumed first. A later bar that opens past a
 *   level fills at its open. The time stop sells at the close of the bar
 *   where hours held (entry bar counted as 1) reaches 168.
 * - After an exit, the coin waits 168 hours before a new signal can count, so
 *   one long slide is not scored as many crashes.
 */
import type { Candle } from '@whaletail/signal-engine';

export const CRASH_BOUNCE_RULE = {
  id: 'CB1',
  label: 'Crash Bounce: buy a 25% drop from the 7-day high',
  dropPct: 0.25,
  lookbackH: 168,
  targetPct: 0.1,
  stopPct: 0.3,
  maxHoldH: 168,
  /** Only signals on bars starting at or after this instant count. */
  registeredAtMs: Date.parse('2026-10-09T00:00:00Z'),
  /**
   * Coinbase USD markets watched: the backtest's coins (the 20 largest on
   * 2023-01-01 that trade on Coinbase, minus MATIC, which became POL) plus
   * today's top-20 coins that trade on Coinbase. Fixed at registration.
   */
  coins: [
    'BTC',
    'ETH',
    'XRP',
    'SOL',
    'BNB',
    'DOGE',
    'ADA',
    'LINK',
    'XLM',
    'LTC',
    'BCH',
    'HYPE',
    'ZEC',
    'NEAR',
    'UNI',
    'DOT',
    'AVAX',
    'SHIB',
    'ATOM',
    'ETC',
    'TON',
  ],
} as const;

export type CrashCoin = (typeof CRASH_BOUNCE_RULE.coins)[number];

/** The rule's numbers, loosened from literals so tests can vary them. */
export interface CrashRuleParams {
  dropPct: number;
  lookbackH: number;
  targetPct: number;
  stopPct: number;
  maxHoldH: number;
  registeredAtMs: number;
}

/** Per-side costs. Slippage is charged on every fill; fees at two levels. */
export const CRASH_BOUNCE_COSTS = {
  slippagePerSide: 0.0005,
  /** Roughly Coinbase Advanced's lowest-tier maker fee. */
  feeLow: 0.006,
  /** Roughly its lowest-tier taker fee; the pass bar is judged here. */
  feeHigh: 0.012,
} as const;

/**
 * Set before any forward data existed. Crashes come in clusters (one market
 * crash makes many coins trigger in the same hour), so the bar counts
 * distinct weeks as well as trades, and the confidence interval resamples
 * whole weeks.
 */
export const CRASH_BOUNCE_PASS_BAR = {
  minTrades: 30,
  minWeeks: 8,
  /** Mean net return per trade at the high fee must be above zero... */
  feePerSide: CRASH_BOUNCE_COSTS.feeHigh,
  /** ...and so must the lower end of its week-clustered 95% interval. */
  minLowerBound: 0,
  /** No stop may fill more than this far past the −30% stop (fill quality). */
  maxStopOvershoot: 0.1,
} as const;

/** Notional per trade for the dollar P&L figures. */
export const CRASH_BOUNCE_STAKE_DOLLARS = 1_000;

const HOUR_MS = 3_600_000;

export type CrashExitReason = 'target' | 'stop' | 'time';

export interface CrashTrade {
  coin: string;
  /** Start of the bar whose close triggered the signal. */
  signalBarMs: number;
  signalClose: number;
  /** The 7-day high the drop was measured from. */
  rollingHigh: number;
  /** Start of the entry bar; the fill is its open. */
  entryMs: number;
  /** Fill price incl. slippage. */
  entryPx: number;
  /** Start of the exit bar. Absent while the trade is open. */
  exitMs?: number;
  /** Fill price incl. slippage. */
  exitPx?: number;
  reason?: CrashExitReason;
}

interface PendingEntry {
  signalBarMs: number;
  signalClose: number;
  rollingHigh: number;
}

export interface CrashCoinState {
  /** Start of the last bar processed. */
  lastBarMs: number | null;
  lastClose: number | null;
  /** The 7-day high at the last bar, and how far below it the close was. */
  rollingHigh: number | null;
  drawdown: number | null;
  /** A signal waiting for the next bar's open. */
  pending: PendingEntry | null;
  open: CrashTrade | null;
  /** No new signal from a bar starting before this. */
  cooldownUntilMs: number;
}

export interface CrashBounceState {
  schemaVersion: 1;
  coins: Record<string, CrashCoinState>;
  /** Closed trades, oldest first. */
  trades: CrashTrade[];
  lastRunMs: number | null;
  /** Coins whose candles could not be fetched on the last run. */
  lastFailures: string[];
}

export function emptyCoinState(): CrashCoinState {
  return {
    lastBarMs: null,
    lastClose: null,
    rollingHigh: null,
    drawdown: null,
    pending: null,
    open: null,
    cooldownUntilMs: 0,
  };
}

export function emptyCrashBounceState(): CrashBounceState {
  return { schemaVersion: 1, coins: {}, trades: [], lastRunMs: null, lastFailures: [] };
}

/** Rarely more than a few dozen trades a year; this is decades. */
export const MAX_CRASH_TRADES = 5_000;

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

function parseTrade(raw: unknown): CrashTrade | null {
  const t = raw as Partial<CrashTrade> | null;
  if (
    !t ||
    typeof t.coin !== 'string' ||
    !isNum(t.signalBarMs) ||
    !isNum(t.signalClose) ||
    !isNum(t.rollingHigh) ||
    !isNum(t.entryMs) ||
    !isNum(t.entryPx)
  ) {
    return null;
  }
  const trade: CrashTrade = {
    coin: t.coin,
    signalBarMs: t.signalBarMs,
    signalClose: t.signalClose,
    rollingHigh: t.rollingHigh,
    entryMs: t.entryMs,
    entryPx: t.entryPx,
  };
  if (
    isNum(t.exitMs) &&
    isNum(t.exitPx) &&
    (t.reason === 'target' || t.reason === 'stop' || t.reason === 'time')
  ) {
    trade.exitMs = t.exitMs;
    trade.exitPx = t.exitPx;
    trade.reason = t.reason;
  }
  return trade;
}

function parseCoin(raw: unknown): CrashCoinState {
  const c = raw as Partial<CrashCoinState> | null;
  const s = emptyCoinState();
  if (!c) return s;
  if (isNum(c.lastBarMs)) s.lastBarMs = c.lastBarMs;
  if (isNum(c.lastClose)) s.lastClose = c.lastClose;
  if (isNum(c.rollingHigh)) s.rollingHigh = c.rollingHigh;
  if (isNum(c.drawdown)) s.drawdown = c.drawdown;
  if (isNum(c.cooldownUntilMs)) s.cooldownUntilMs = c.cooldownUntilMs;
  const p = c.pending;
  if (p && isNum(p.signalBarMs) && isNum(p.signalClose) && isNum(p.rollingHigh)) {
    s.pending = {
      signalBarMs: p.signalBarMs,
      signalClose: p.signalClose,
      rollingHigh: p.rollingHigh,
    };
  }
  const open = parseTrade(c.open);
  if (open && open.reason === undefined) s.open = open;
  return s;
}

export function parseCrashBounceState(raw: string): CrashBounceState {
  try {
    const parsed = JSON.parse(raw) as Partial<CrashBounceState>;
    if (parsed?.schemaVersion !== 1) return emptyCrashBounceState();
    const coins: Record<string, CrashCoinState> = {};
    for (const [k, v] of Object.entries(parsed.coins ?? {})) coins[k] = parseCoin(v);
    const trades = (Array.isArray(parsed.trades) ? parsed.trades : [])
      .map(parseTrade)
      .filter((t): t is CrashTrade => t !== null && t.reason !== undefined);
    return {
      schemaVersion: 1,
      coins,
      trades,
      lastRunMs: isNum(parsed.lastRunMs) ? parsed.lastRunMs : null,
      lastFailures: Array.isArray(parsed.lastFailures)
        ? parsed.lastFailures.filter((x): x is string => typeof x === 'string')
        : [],
    };
  } catch {
    return emptyCrashBounceState();
  }
}

/** Net return of a round trip at a given fee, from fills incl. slippage. */
export function netReturn(entryPx: number, exitPx: number, feePerSide: number): number {
  return (exitPx * (1 - feePerSide)) / (entryPx * (1 + feePerSide)) - 1;
}

/**
 * Advances one coin through every newly COMPLETED hourly bar. `candles` must
 * be sorted by time and long enough to cover the 7-day window behind each new
 * bar (the caller fetches ~12 days). Bars that have not ended by `nowMs` are
 * ignored, so a run never acts on a close that isn't final. Returns the new
 * coin state and any trades that closed.
 */
export function stepCoin(
  coin: string,
  prev: CrashCoinState,
  candles: readonly Candle[],
  nowMs: number,
  rule: CrashRuleParams = CRASH_BOUNCE_RULE,
  slippage: number = CRASH_BOUNCE_COSTS.slippagePerSide,
): { state: CrashCoinState; closed: CrashTrade[] } {
  const s: CrashCoinState = {
    ...prev,
    pending: prev.pending ? { ...prev.pending } : null,
    open: prev.open ? { ...prev.open } : null,
  };
  const closed: CrashTrade[] = [];
  const L = rule.lookbackH;

  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const tMs = c.time * 1000;
    if (tMs + HOUR_MS > nowMs) break; // still forming
    if (s.lastBarMs !== null && tMs <= s.lastBarMs) continue;

    // 7-day high through this bar, when the window is fully covered.
    let high: number | null = null;
    if (i >= L - 1) {
      high = -Infinity;
      for (let k = i - L + 1; k <= i; k++) high = Math.max(high, candles[k].high);
    }

    let isEntryBar = false;
    if (s.pending && !s.open) {
      s.open = {
        coin,
        signalBarMs: s.pending.signalBarMs,
        signalClose: s.pending.signalClose,
        rollingHigh: s.pending.rollingHigh,
        entryMs: tMs,
        entryPx: c.open * (1 + slippage),
      };
      s.pending = null;
      isEntryBar = true;
    }

    if (s.open) {
      const target = s.open.entryPx * (1 + rule.targetPct);
      const stop = s.open.entryPx * (1 - rule.stopPct);
      let raw: number | null = null;
      let reason: CrashExitReason | null = null;
      if (!isEntryBar && c.open <= stop) [raw, reason] = [c.open, 'stop'];
      else if (!isEntryBar && c.open >= target) [raw, reason] = [c.open, 'target'];
      else if (c.low <= stop) [raw, reason] = [stop, 'stop'];
      else if (c.high >= target) [raw, reason] = [target, 'target'];
      else if ((tMs - s.open.entryMs) / HOUR_MS + 1 >= rule.maxHoldH) {
        [raw, reason] = [c.close, 'time'];
      }
      if (raw !== null && reason !== null) {
        closed.push({ ...s.open, exitMs: tMs, exitPx: raw * (1 - slippage), reason });
        s.open = null;
        s.cooldownUntilMs = tMs + HOUR_MS + L * HOUR_MS;
      }
    } else if (
      !s.pending &&
      high !== null &&
      tMs >= rule.registeredAtMs &&
      tMs >= s.cooldownUntilMs &&
      c.close <= (1 - rule.dropPct) * high
    ) {
      s.pending = { signalBarMs: tMs, signalClose: c.close, rollingHigh: high };
    }

    s.lastBarMs = tMs;
    s.lastClose = c.close;
    if (high !== null) {
      s.rollingHigh = high;
      s.drawdown = 1 - c.close / high;
    }
  }
  return { state: s, closed };
}

/** Deterministic PRNG (mulberry32), so the interval doesn't jitter per request. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Monday-start week index (the Unix epoch is a Thursday). */
export function weekOf(ms: number): number {
  return Math.floor((ms - 4 * 86_400_000) / (7 * 86_400_000));
}

/**
 * 95% bootstrap interval of the mean, resampling whole clusters, so many
 * coins crashing in the same week count as one event. Null with < 2 clusters.
 */
export function clusterMeanInterval(
  values: readonly number[],
  clusters: readonly number[],
  iters = 2000,
  seed = 7,
): [number, number] | null {
  const groups = new Map<number, number[]>();
  values.forEach((v, i) => {
    const g = groups.get(clusters[i]) ?? [];
    g.push(v);
    groups.set(clusters[i], g);
  });
  const list = [...groups.values()];
  if (list.length < 2) return null;
  const r = rng(seed);
  const means: number[] = [];
  for (let it = 0; it < iters; it++) {
    let sum = 0;
    let n = 0;
    for (let g = 0; g < list.length; g++) {
      const grp = list[Math.floor(r() * list.length)];
      for (const v of grp) sum += v;
      n += grp.length;
    }
    means.push(sum / n);
  }
  means.sort((a, b) => a - b);
  return [means[Math.floor(0.025 * iters)], means[Math.floor(0.975 * iters)]];
}

export type CrashVerdict = 'collecting' | 'passing' | 'failing';

export interface CrashBounceSummary {
  trades: number;
  weeks: number;
  open: number;
  winRate: number | null;
  /** Mean net return per trade, at the low and high fee. */
  meanNetLow: number | null;
  meanNetHigh: number | null;
  /** Week-clustered 95% interval of meanNetHigh. */
  intervalHigh: [number, number] | null;
  /** Total P&L at CRASH_BOUNCE_STAKE_DOLLARS per trade, low and high fee. */
  pnlLow: number;
  pnlHigh: number;
  /** Largest amount a stop filled beyond −30%, as a fraction (0 = none). */
  maxStopOvershoot: number;
  verdict: CrashVerdict;
}

export function summarizeCrashBounce(state: CrashBounceState): CrashBounceSummary {
  const closed = state.trades.filter(
    (t): t is Required<CrashTrade> => t.exitPx !== undefined,
  );
  const open = Object.values(state.coins).filter((c) => c.open).length;
  const low = closed.map((t) =>
    netReturn(t.entryPx, t.exitPx, CRASH_BOUNCE_COSTS.feeLow),
  );
  const high = closed.map((t) =>
    netReturn(t.entryPx, t.exitPx, CRASH_BOUNCE_COSTS.feeHigh),
  );
  const weeks = new Set(closed.map((t) => weekOf(t.entryMs))).size;
  const mean = (a: number[]) =>
    a.length ? a.reduce((x, y) => x + y, 0) / a.length : null;
  const meanHigh = mean(high);
  const interval = clusterMeanInterval(
    high,
    closed.map((t) => weekOf(t.entryMs)),
  );
  let maxStopOvershoot = 0;
  for (const t of closed) {
    if (t.reason !== 'stop') continue;
    const gross = t.exitPx / t.entryPx - 1;
    maxStopOvershoot = Math.max(maxStopOvershoot, -CRASH_BOUNCE_RULE.stopPct - gross);
  }

  const bar = CRASH_BOUNCE_PASS_BAR;
  let verdict: CrashVerdict = 'collecting';
  if (maxStopOvershoot > bar.maxStopOvershoot) verdict = 'failing';
  else if (closed.length >= bar.minTrades && weeks >= bar.minWeeks) {
    verdict =
      meanHigh !== null &&
      meanHigh > 0 &&
      interval !== null &&
      interval[0] > bar.minLowerBound
        ? 'passing'
        : 'failing';
  }

  return {
    trades: closed.length,
    weeks,
    open,
    winRate: closed.length ? high.filter((x) => x > 0).length / closed.length : null,
    meanNetLow: mean(low),
    meanNetHigh: meanHigh,
    intervalHigh: interval,
    pnlLow: low.reduce((a, b) => a + b, 0) * CRASH_BOUNCE_STAKE_DOLLARS,
    pnlHigh: high.reduce((a, b) => a + b, 0) * CRASH_BOUNCE_STAKE_DOLLARS,
    maxStopOvershoot,
    verdict,
  };
}

export interface CrashWatchRow {
  coin: string;
  lastBarMs: number | null;
  lastClose: number | null;
  rollingHigh: number | null;
  /** How far the last close sits below the 7-day high (0.25 triggers). */
  drawdown: number | null;
  status: 'watching' | 'pending' | 'holding' | 'cooldown' | 'no data';
  cooldownUntilMs: number | null;
  open: CrashTrade | null;
  /** Unrealized gross return of the open trade at the last close. */
  unrealized: number | null;
}

/** One row per watched coin, deepest drawdown first. */
export function crashWatchlist(state: CrashBounceState): CrashWatchRow[] {
  const rows = CRASH_BOUNCE_RULE.coins.map((coin): CrashWatchRow => {
    const c = state.coins[coin];
    if (!c || c.lastBarMs === null) {
      return {
        coin,
        lastBarMs: null,
        lastClose: null,
        rollingHigh: null,
        drawdown: null,
        status: 'no data',
        cooldownUntilMs: null,
        open: null,
        unrealized: null,
      };
    }
    const status: CrashWatchRow['status'] = c.open
      ? 'holding'
      : c.pending
        ? 'pending'
        : c.lastBarMs + HOUR_MS < c.cooldownUntilMs
          ? 'cooldown'
          : 'watching';
    return {
      coin,
      lastBarMs: c.lastBarMs,
      lastClose: c.lastClose,
      rollingHigh: c.rollingHigh,
      drawdown: c.drawdown,
      status,
      cooldownUntilMs: status === 'cooldown' ? c.cooldownUntilMs : null,
      open: c.open,
      unrealized:
        c.open && c.lastClose !== null
          ? (c.lastClose * (1 - CRASH_BOUNCE_COSTS.slippagePerSide)) / c.open.entryPx - 1
          : null,
    };
  });
  return rows.sort((a, b) => (b.drawdown ?? -1) - (a.drawdown ?? -1));
}
